import EventDispatcher from './EventDispatcher';


/**
 * This is the type of the argument to the `add` method of CommandHistory.
 */
interface UndoFrame {
    /*
     * A unique identifier for this undo frame. If multiple undo frames are
     * added with the same name, they are merged by preserving the old undo and
     * accepting the new redo. If the name is null (or omitted), then it will
     * never be merged with previous frames.
     */
    name?: string;

    /**
     * A string that describes the result of the action currently being
     * comitted to the history. If the previous value was five and the new
     * value is ten, the description should read "set value to ten".
     */
    description: string;

    /**
     * A callable that performs the undo action. The callable should not take
     * any arguments.
     */
    undo: () => void;

    /**
     * A callable that performs the redo action, that is, it undoes the redo.
     * The callable should not take any arguments.
     */
    redo: () => void;
}

/**
 * The command history provides undo/redo support based on higher order
 * functions. Instead of logging concrete instances of a Command class, we save
 * a pair of functions that correspond to undo and redo. The functions could be
 * bound methods of an object, or they could be closures that capture all the
 * necessary state in their environment.
 *
 * Two things this pattern cannot do: 1) keep undo/redo history between browser
 * sessions or 2) redo the last action. Those would require real concrete
 * Command objects, which require a lot more coding on the user's end because
 * every action in the UI must be represented by a class.
 */
export default class CommandHistory extends EventDispatcher {

    private undoStack: UndoFrame[] = new Array();
    private redoStack: UndoFrame[] = new Array();


    /**
    * Most use cases should call app.commit(). For anything out of the
    * ordinary, use the add method to add some history to the undo/redo stack.
    *
    * When a new command is added, the excess is discarded from the end of the
    * command stack, meaning that if you undo some stuff and then do something
    * else, then there's no way to redo anymore. To put it another way,
    * undo/redo is a simple stack data structure, not a tree.
    *
    * The add function raises a CustomEvent on the window object named
    * CommandHistory.add. This allows the UI to listen for updates and set the
    * disabled state of the undo and redo buttons accordingly.
    *
    */
    add(frame: UndoFrame) {
        // empty the redo stack, discard the contents
        this.redoStack.length = 0;

        // last may be null if we have a zero length undo stack
        const last = this.undoStack[this.undoStack.length-1];

        // If the new frame has a name string and it matches the last frame's
        // name, then we merge the two.
        if(last && frame.name != null && last.name === frame.name) {
            const newFrame = Object.assign({}, frame);
            newFrame.undo = last.undo;
            this.undoStack.pop();
            this.undoStack.push(newFrame);
        } else {
            this.undoStack.push(frame);
        }

        this.trigger('add');
    }

    /**
     * Calling undo on the command history undoes the last action that was added
     * to the stack. After an action has been undone, canRedo should return
     * true until another command is added to the stack. If there's nothing in
     * the stack, or we've already undone all the commands in the stack, then
     * calling undo again does nothing.
     */
    undo() {
        if(this.undoStack.length > 0){
            var frame = this.undoStack.pop();
            this.redoStack.push(frame);
            frame.undo();
            this.trigger('undo');
        }
    }

    /**
     * Once undo has been called, it is possible to "undo the undo" with a redo.
     * Each redo should be paired with a matching undo. If we don't have
     * anything to redo (canRedo is false), this does nothing.
     */
    redo() {
        if(this.redoStack.length > 0){
            var frame = this.redoStack.pop();
            this.undoStack.push(frame);
            frame.redo();
            this.trigger('redo');
        }
    }

    /**
     * If nothing has been added to the command history, or if we've already
     * undone all of the things in the command history, then we're unable to
     * undo anymore. This method is useful for setting the enabled or disabled
     * state of the undo button.
     * @returns Whether or not calling undo will have any effect.
     */
    canUndo(): boolean {
        return this.undoStack.length > 0;
    }

    /**
     * This method is useful for setting the enabled or disabled state of the
     * undo button. The only times that the redo method will have an effect is
     * basically just after we have undone something.
     * @return Whether or not calling redo will have any effect.
     */
    canRedo(): boolean {
        return this.redoStack.length > 0;
    }

    /**
     * Get a tool tip for the undo button that shows what action will be
     * undone.
     */
    getUndoDescription(): string {
        if(this.undoStack.length > 0) {
            return "Undo " + this.undoStack[this.undoStack.length - 1].description;
        } else {
            return "Nothing to undo";
        }
    }

    /**
     * Get a tool tip for the redo button that shows what action will be
     * redone.
     */
    getRedoDescription(): string {
        if(this.redoStack.length > 0) {
            return "Redo " + this.redoStack[this.redoStack.length - 1].description;
        } else {
            return "Nothing to redo";
        }
    }
};
